// @mui
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import { useMemo } from 'react';
import { HashLink } from 'react-router-hash-link';
// routes
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
// hooks
import useContactUsHref from 'src/hooks/use-contact-us-href';
// _mock
import { _socials } from 'src/_mock';
// components
import Iconify from 'src/components/iconify';
import Logo from 'src/components/logo';
import { PROJECT_NAME, SUPPORT_EMAIL } from 'src/config-global';

// ----------------------------------------------------------------------

const getFooterLinks = (contactHref) => [
  {
    headline: PROJECT_NAME,
    children: [
      { name: 'Contact us', href: contactHref },
      { name: 'FAQs', href: '/#faqs' },
    ],
  },
  {
    headline: 'Legal',
    children: [
      { name: 'Terms and Condition', href: paths.termsAndConditions },
      { name: 'Privacy Policy', href: paths.privacyPolicy },
    ],
  },
  {
    headline: 'Contact',
    children: [{ name: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` }],
  },
];

const isHashHref = (href = '') => href.includes('#') && !href.startsWith('mailto:');

// ----------------------------------------------------------------------

export default function Footer() {
  const contactHref = useContactUsHref();
  const links = useMemo(() => getFooterLinks(contactHref), [contactHref]);
  const date = new Date();
  const year = date.getFullYear();

  // const isHome = pathname === '/';

  // const simpleFooter = (
  //   <Box
  //     component="footer"
  //     sx={{
  //       py: 5,
  //       textAlign: 'center',
  //       position: 'relative',
  //       bgcolor: 'background.default',
  //     }}
  //   >
  //     <Container>
  //       <Logo sx={{ mb: 1, mx: 'auto' }} />

  //       <Typography variant="caption" component="div">
  //         © All rights reserved
  //         <br /> made by
  //         <Link href="https://minimals.cc/"> minimals.cc </Link>
  //       </Typography>
  //     </Container>
  //   </Box>
  // );

  const mainFooter = (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        bgcolor: 'background.default',
      }}
    >
      <Divider />

      <Container
        sx={{
          pt: 10,
          pb: 5,
          textAlign: { xs: 'center', md: 'unset' },
        }}
      >
        <Logo sx={{
          mb: 3,
          width: 270,
          height: 140,
        }} />

        <Grid
          container
          justifyContent={{
            xs: 'center',
            md: 'space-between',
          }}
        >
          <Grid xs={8} md={3}>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 270,
                mx: { xs: 'auto', md: 'unset' },
              }}
            >
              {PROJECT_NAME} is an all-in-one CRM designed for mobile wash and exterior cleaning businesses. Manage jobs, create better estimates, track customers, schedule crews, send quotes and invoices, and get paid faster — all from a simple, mobile-friendly dashboard. Spend less time on paperwork and more time making properties shine.

            </Typography>

            <Stack
              direction="row"
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              sx={{
                mt: 3,
                mb: { xs: 5, md: 0 },
              }}
            >
              {_socials.map((social) => (
                <IconButton
                  key={social.name}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(social.color, 0.08),
                    },
                  }}
                >
                  <Iconify color={social.color} icon={social.icon} />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          <Grid xs={12} md={6}>
            <Stack spacing={5} direction={{ xs: 'column', md: 'row' }}>
              {links.map((list) => (
                <Stack
                  key={list.headline}
                  spacing={2}
                  alignItems={{ xs: 'center', md: 'flex-start' }}
                  sx={{ width: 1 }}
                >
                  <Typography component="div" variant="overline">
                    {list.headline}
                  </Typography>

                  {list.children.map((link) => {
                    const isMailto = link.href.startsWith('mailto:');
                    const isExternal =
                      link.href.startsWith('http') || link.href.startsWith('https');

                    return (
                      <div key={`${link.name}-${link.href}`}>
                        {isHashHref(link.href) ? (
                          <Link
                            component={HashLink}
                            to={link.href}
                            href={link.href}
                            smooth
                            sx={{ textDecoration: 'none' }}
                            color="inherit"
                            variant="body2"
                          >
                            {link.name}
                          </Link>
                        ) : (
                          <Link
                            component={isMailto || isExternal ? 'a' : RouterLink}
                            href={link.href}
                            color="inherit"
                            variant="body2"
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                          >
                            {link.name}
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </Stack>
              ))}
            </Stack>

          </Grid>
        </Grid>

        <Typography variant="body2" sx={{ mt: 10 }}>
          © {year}. All rights reserved .
        </Typography>
      </Container>
    </Box>
  );

  // return isHome ? simpleFooter : mainFooter;
  return mainFooter;
}
